import { MinusCircleIcon, WarningCircleIcon, WarningIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  metricContradictions,
  metricKey,
  metricNumber,
  openQuestions,
  questionKey,
  readinessQuote,
  refusalTopics,
  answerStatusKey,
  contradictionKey,
  type SellerMetric,
  type SellerMetrics as SellerMetricsData,
} from '@entities/negotiation';
import { Badge } from '@shared/ui';

import styles from './SellerMetrics.module.css';

/** Порог, ниже которого время ответа читается в минутах, а не в часах. */
const MINUTES_IN_HOUR = 60;

interface SellerMetricsProps {
  metrics: SellerMetricsData | null;
}

/**
 * Профиль коммуникации автохауса (FE-240, MVP_PRD §37).
 *
 * **Блок отвечает на один вопрос: как автохаус вёл переписку.** За сколько
 * рабочих часов ответил, на сколько вопросов из заданных, на скольких
 * отказался, сколько раз пришлось напомнить. Он **ничего не утверждает о самом
 * продавце** — ни о надёжности, ни о добросовестности, ни о безопасности
 * сделки: CarViQ.DE не проверяет продавцов, и интерфейс, который делает вид, что
 * проверил, вводит пользователя в заблуждение относительно природы услуги
 * (§37.1).
 *
 * Четыре решения разметки, ни одно из которых не косметическое:
 *
 * * **общего числа нет.** Метрики стоят отдельными строками и не сводятся ни в
 *   балл, ни в звёзды, ни в «уровень отзывчивости»: это был бы тот самый
 *   агрегат-вердикт, которого в продукте не существует;
 * * **блок визуально отделён от характеристик автомобиля** (§37.3): своя
 *   рамка, свой фон, свой заголовок. «Какой автомобиль» и «как отвечал
 *   автохаус» — разные вопросы, и смешивать их нельзя даже вёрсткой;
 * * **отказ отвечать назван данными, а не приговором.** Рядом со строкой стоит
 *   прямая оговорка (§36.4), а темы отказов раскрыты дословными цитатами;
 * * **готовность к осмотру показывается цитатой, а не ярлыком.** Ни «готов», ни
 *   «не готов»: отличить «Begutachtung jederzeit möglich» от «Begutachtung nur
 *   nach Kaufvertrag» можно только истолковав фразу, а толкований CarViQ.DE не
 *   делает (CLAUDE.md §2).
 *
 * Компонент ничего не решает: какие метрики показывать, решает backend списком
 * `metrics` — в него попадают только те, у которых есть основание.
 *
 * **Цвет — семантическая шкала DESIGN_SYSTEM §2.4, и только там, где у
 * значения есть смысл шкалы** (`toneOf` ниже): отказы отвечать — «компромисс»,
 * как и в блоке проверки; расхождения — «требует внимания» (§8); отсутствие
 * ответа и данных — «нет данных». Скорость, отвеченность и напоминания —
 * измерения без оценки: они стоят обычным текстом, потому что «быстро» и
 * «медленно» — уже вердикт. Зелёного здесь нет вовсе: «соответствует» —
 * утверждение о критерии, а у манеры переписки критериев нет.
 */
export function SellerMetrics({ metrics }: SellerMetricsProps) {
  const { t } = useTranslation();

  // Профиля ещё нет: автохаусу не писали. Пустой блок с нулями читался бы как
  // «автохаус не отвечает», а на деле его ещё ни о чём не спрашивали.
  if (metrics === null || metrics.metrics.length === 0) return null;

  return (
    <section className={styles.wrap} aria-label={t('negotiation.metrics.title')}>
      <h2 className={styles.title}>{t('negotiation.metrics.title')}</h2>
      {/* Что здесь измеряется и чего здесь нет — до чисел, а не после. */}
      <p className={styles.note}>{t('negotiation.metrics.note')}</p>

      <ul className={styles.list}>
        {metrics.metrics.map((metric) => (
          <li key={metric.code} className={styles.item}>
            <MetricRow metric={metric} />
          </li>
        ))}
      </ul>
      {/* Версии расчёта на экране нет (12.09.2026): `calculation_version`
          остаётся в контракте и в аудите, пользователю она ничего не говорит. */}
    </section>
  );
}

type MetricTone = 'compromise' | 'attention' | 'nodata' | null;

const TONE_ICON: Record<Exclude<MetricTone, null>, ReactNode> = {
  compromise: <WarningIcon weight="bold" />,
  attention: <WarningCircleIcon weight="bold" />,
  nodata: <MinusCircleIcon weight="bold" />,
};

/**
 * Тон значения по шкале §2.4. `null` — измерение без оценки, обычный текст.
 *
 * Ноль отказов и ноль расхождений тоже без цвета: зелёная «ноль» читалась бы
 * как «продавец хороший», а это вердикт, которого нет (§37.1).
 */
function toneOf(metric: SellerMetric, value: string | null): MetricTone {
  if (value === null) return 'nodata';
  switch (metric.code) {
    case 'REFUSALS':
      return (metricNumber(metric, 'count') ?? 0) > 0 ? 'compromise' : null;
    case 'CONTRADICTIONS':
      return (metricNumber(metric, 'count') ?? 0) > 0 ? 'attention' : null;
    case 'INSPECTION_READINESS':
    case 'MEDIA_READINESS':
      return readinessQuote(metric) === null ? 'nodata' : null;
    default:
      return null;
  }
}

function MetricRow({ metric }: { metric: SellerMetric }) {
  const { t } = useTranslation();
  const value = useMetricValue(metric);
  const tone = toneOf(metric, value);
  const text = value ?? t('negotiation.metrics.noData');

  return (
    <>
      <p className={styles.head}>
        <span className={styles.label}>{t(metricKey(metric.code))}</span>
        {tone === null ? (
          <span className={styles.value}>{text}</span>
        ) : (
          <Badge tone={tone} icon={TONE_ICON[tone]} uppercase={false}>
            {text}
          </Badge>
        )}
      </p>
      <MetricDetail metric={metric} />
    </>
  );
}

/**
 * Значение строки. `null` — «Нет данных».
 *
 * Строки собираются из чисел и кодов, пришедших с сервера: готового текста
 * backend не присылает (CLAUDE.md §6).
 */
function useMetricValue(metric: SellerMetric): string | null {
  const { t } = useTranslation();

  switch (metric.code) {
    case 'FIRST_RESPONSE':
    case 'MEDIAN_RESPONSE': {
      const minutes = metricNumber(metric, 'minutes');
      if (minutes === null) return null;
      // Рабочие, а не календарные (§34.8): ночь, выходные и федеральные
      // праздники в скорость ответа не записываются, и подпись обязана это
      // называть — иначе «60 минут» читается как час по часам на стене.
      return minutes < MINUTES_IN_HOUR
        ? t('negotiation.metrics.value.minutes', { count: minutes })
        : t('negotiation.metrics.value.hours', { count: Math.round(minutes / MINUTES_IN_HOUR) });
    }
    case 'ANSWER_COVERAGE': {
      const answered = metricNumber(metric, 'answered');
      const asked = metricNumber(metric, 'asked');
      if (answered === null || asked === null) return null;
      return t('negotiation.metrics.value.coverage', { answered, asked });
    }
    case 'REFUSALS':
    case 'CONTRADICTIONS':
    case 'REMINDERS': {
      const count = metricNumber(metric, 'count');
      return count === null ? null : t('negotiation.metrics.value.count', { count });
    }
    case 'INSPECTION_READINESS':
    case 'MEDIA_READINESS': {
      // Ответ по существу показывается цитатой ниже, а в значении стоит
      // состояние вопроса: «ответил» или «ответа нет».
      return readinessQuote(metric) === null
        ? t('negotiation.metrics.value.noAnswer')
        : t('negotiation.metrics.value.answered');
    }
    default:
      return null;
  }
}

/**
 * Раскрытие строки до основания: цитаты, темы, обе стороны расхождения.
 *
 * «Не стал отвечать о: аварийная история» без самой фразы автохауса — это
 * пересказ; здесь стоит то, что он написал.
 */
function MetricDetail({ metric }: { metric: SellerMetric }) {
  const { t } = useTranslation();

  if (metric.code === 'REFUSALS') {
    const topics = refusalTopics(metric);
    if (topics.length === 0) return null;
    return (
      <>
        <ul className={styles.topics}>
          {topics.map((topic) => (
            <li key={topic.question_code} className={styles.topic}>
              {t(questionKey(topic.question_code))}
              <figure className={styles.quoteBlock}>
                <blockquote className={styles.quote}>{topic.quote}</blockquote>
                <figcaption className={styles.quoteNote}>
                  {t('negotiation.check.quoteNote')}
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>
        {/* Отказ — данные, а не приговор автомобилю (§36.4). */}
        <p className={styles.hint}>{t('negotiation.check.refusedNote')}</p>
      </>
    );
  }

  if (metric.code === 'CONTRADICTIONS') {
    const items = metricContradictions(metric);
    if (items.length === 0) return null;
    return (
      <>
        <ul className={styles.topics}>
          {items.map((item) => (
            <li key={`${item.question_code}-${item.code}`} className={styles.topic}>
              {t(questionKey(item.question_code))}
              {' · '}
              {t(contradictionKey(item.code))}
            </li>
          ))}
        </ul>
        {/* CarViQ.DE показывает факт и молчит о причине: обе цитаты стоят рядом
            в блоке проверки, здесь — только сам факт расхождения (§36.5). */}
        <p className={styles.hint}>{t('negotiation.check.discrepancyNote')}</p>
      </>
    );
  }

  if (metric.code === 'INSPECTION_READINESS' || metric.code === 'MEDIA_READINESS') {
    const quote = readinessQuote(metric);
    if (quote === null) return <p className={styles.hint}>{t('negotiation.check.silence')}</p>;
    return (
      <figure className={styles.quoteBlock}>
        <blockquote className={styles.quote}>{quote}</blockquote>
        <figcaption className={styles.quoteNote}>{t('negotiation.check.quoteNote')}</figcaption>
      </figure>
    );
  }

  if (metric.code === 'ANSWER_COVERAGE') {
    const open = openQuestions(metric);
    if (open.length === 0) return null;
    return (
      <ul className={styles.topics}>
        {open.map((item) => (
          <li key={item.question_code} className={styles.topic}>
            {t(questionKey(item.question_code))}
            {' — '}
            {t(answerStatusKey(item.status))}
          </li>
        ))}
      </ul>
    );
  }

  return null;
}
